import { createFileRoute, Link } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { useState } from "react";
import { CTABlock } from "@/components/CTABlock";
import { StaggerContainer, StaggerItem } from "@/components/AnimatedSection";
import { GalleryLightbox, type LightboxItem } from "@/components/GalleryLightbox";
import { listPublicGalleryPhotos, type GalleryPhoto } from "@/lib/gallery.functions";
import { seoHead } from "@/lib/seo";


const fallbackItems = [
  { src: "/gallery/workshop-mercedes-amg.jpg", alt: "Mercedes AMG E63 in for an MOT at Woodseer Autos in Poplar", category: "MOT", caption: null as string | null },
  { src: "/gallery/workshop-audi-s4.jpg", alt: "Audi S4 in our workshop for paint and panel repair", category: "Bodywork", caption: null },
  { src: "/gallery/workshop-mclaren.jpg", alt: "McLaren parked outside the Woodseer Autos workshop in E14", category: "Workshop", caption: null },
  { src: "/gallery/workshop-bentley.jpg", alt: "Bentley Continental GT booked in for a service", category: "Workshop", caption: null },
  { src: "/gallery/workshop-rolls-royce.jpg", alt: "Rolls-Royce Ghost in the MOT bay", category: "MOT", caption: null },
  { src: "/gallery/workshop-mercedes-s-class.jpg", alt: "Mercedes S-Class on the ramp during a major service", category: "Servicing", caption: null },
  { src: "/gallery/workshop-e63-amg.jpg", alt: "Mercedes E63 AMG hooked up for engine diagnostics", category: "Diagnostics", caption: null },
  { src: "/gallery/workshop-porsche-ramp.jpg", alt: "Porsche Panamera up on the ramp for an MOT", category: "MOT", caption: null },
];

const searchSchema = z.object({
  category: fallback(z.string(), "All").default("All"),
  featured: fallback(z.boolean(), false).default(false),
});
type GallerySearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/gallery")({
  validateSearch: zodValidator(searchSchema),
  loader: async () => {
    const r = await listPublicGalleryPhotos({ data: {} });
    return { photos: r.photos };
  },
  head: ({ loaderData }) => {
    const photos = loaderData?.photos ?? [];
    const items = photos.length > 0
      ? photos.map((p) => ({
          url: p.public_url,
          alt: p.alt_text,
          caption: p.caption || p.alt_text,
          width: p.width,
          height: p.height,
          thumb: p.variants?.thumb?.url,
        }))
      : fallbackItems.map((f) => ({ url: `https://woodseerautos.co.uk${f.src}`, alt: f.alt, caption: f.alt, width: undefined, height: undefined, thumb: undefined }));

    const imageGallerySchema = {
      "@context": "https://schema.org",
      "@type": "ImageGallery",
      name: "Woodseer Autos workshop photos",
      description: "Photos from inside the Woodseer Autos workshop in Poplar, East London — MOTs, services, repairs and diagnostics.",
      url: "https://woodseerautos.co.uk/gallery",
      image: items.slice(0, 24).map((it) => ({
        "@type": "ImageObject",
        contentUrl: it.url,
        url: it.url,
        name: it.alt,
        description: it.caption,
        ...(it.thumb ? { thumbnailUrl: it.thumb } : {}),
        ...(it.width ? { width: it.width } : {}),
        ...(it.height ? { height: it.height } : {}),
        creditText: "Woodseer Autos",
        creator: { "@type": "Organization", name: "Woodseer Autos" },
        copyrightNotice: "© Woodseer Autos",
      })),
    };

    return seoHead({
      path: "/gallery",
      title: "Workshop Photos — See Our Day-to-Day in Poplar | Woodseer Autos E14",
      description:
        "Photos from inside our Poplar workshop — MOTs, services, repairs, diagnostics and the cars we look after. Real jobs, real customers, no stock images.",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Workshop Photos", path: "/gallery" },
      ],
      jsonLd: [imageGallerySchema],
    });
  },
  component: GalleryPage,
});

interface DisplayItem {
  src: string;
  srcSet?: string;
  sizes?: string;
  alt: string;
  category: string;
  caption: string | null;
  width?: number;
  height?: number;
  featured?: boolean;
}

function toDisplay(p: GalleryPhoto): DisplayItem {
  const thumb = p.variants?.thumb?.url;
  const medium = p.variants?.medium?.url;
  const srcSetParts: string[] = [];
  if (thumb && p.variants?.thumb?.width) srcSetParts.push(`${thumb} ${p.variants.thumb.width}w`);
  if (medium && p.variants?.medium?.width) srcSetParts.push(`${medium} ${p.variants.medium.width}w`);
  if (p.public_url && p.width) srcSetParts.push(`${p.public_url} ${p.width}w`);
  return {
    src: thumb || medium || p.public_url,
    srcSet: srcSetParts.length ? srcSetParts.join(", ") : undefined,
    sizes: "(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw",
    alt: p.alt_text,
    category: p.category,
    caption: p.caption,
    width: p.width || undefined,
    height: p.height || undefined,
    featured: p.featured,
  };
}

function GalleryPage() {
  const { photos } = Route.useLoaderData();
  const { category, featured } = Route.useSearch();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const items: DisplayItem[] =
    photos.length > 0
      ? photos.map(toDisplay)
      : fallbackItems.map((f) => ({ src: f.src, alt: f.alt, category: f.category, caption: f.caption }));

  const categories = ["All", ...Array.from(new Set(items.map((i) => i.category)))];
  const filtered = items.filter((i) => {
    if (category !== "All" && i.category !== category) return false;
    if (featured && !i.featured) return false;
    return true;
  });

  const lightboxItems: LightboxItem[] = filtered.map((f) => ({
    src: f.src,
    srcSet: f.srcSet,
    sizes: f.sizes,
    alt: f.alt,
    caption: f.caption,
    category: f.category,
    width: f.width,
    height: f.height,
  }));

  return (
    <>
      <section className="section-y">
        <div className="mx-auto max-w-4xl px-4 md:px-6 text-center">
          <p className="eyebrow">Inside the workshop</p>
          <h1 className="mt-4 display-xl text-foreground">Real photos from real jobs.</h1>
          <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            Whatever pulls onto the ramp gets the same care — daily runabout or weekend toy. These are pictures from our workshop in Poplar, not stock images.
          </p>
        </div>
      </section>

      <section className="pb-20 md:pb-24">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <h2 className="sr-only">Workshop photo gallery{category !== "All" ? ` — ${category}` : ""}</h2>
          <div className="flex flex-wrap justify-center items-center gap-2 mb-8">
            {categories.map((cat) => (
              <Link
                key={cat}
                from="/gallery"
                search={(prev: GallerySearch) => ({ ...prev, category: cat })}
                className={`px-4 py-2 rounded-lg text-[12.5px] font-bold tracking-tight transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
                  category === cat
                    ? "bg-primary text-primary-foreground shadow-md shadow-primary/25"
                    : "bg-card border border-border text-foreground/75 hover:text-foreground hover:border-primary/30"
                }`}
              >
                {cat}
              </Link>
            ))}
          </div>
          <div className="flex justify-center mb-10">
            <Link
              from="/gallery"
              search={(prev: GallerySearch) => ({ ...prev, featured: !featured })}
              className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
                featured
                  ? "bg-primary/10 text-primary border-primary/30"
                  : "bg-card text-muted-foreground border-border hover:text-foreground"
              }`}
              aria-pressed={featured}
            >
              <span className={`h-2 w-2 rounded-full ${featured ? "bg-primary" : "bg-muted-foreground/40"}`} />
              Featured only
            </Link>
          </div>

          {filtered.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-12">
              No photos match these filters.{" "}
              <Link from="/gallery" search={{ category: "All", featured: false }} className="text-primary font-bold underline">
                Reset
              </Link>
            </p>
          ) : (
            <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((item, i) => (
                <StaggerItem key={`${item.src}-${i}`}>
                  <button
                    type="button"
                    onClick={() => setLightboxIndex(i)}
                    aria-label={`Open photo ${i + 1} of ${filtered.length}: ${item.alt}`}
                    data-gallery-thumb={i}
                    className="group relative block w-full overflow-hidden rounded-2xl border border-border aspect-[3/2] shadow-lg shadow-black/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background text-left"
                  >
                    <figure className="contents">
                      <img
                        src={item.src}
                        srcSet={item.srcSet}
                        sizes={item.sizes}
                        alt={item.alt}
                        width={item.width}
                        height={item.height}
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-cover transition-transform duration-700 motion-safe:group-hover:scale-105 motion-reduce:transition-none"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-foreground/85 via-foreground/20 to-transparent opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity flex items-end p-5">
                        <figcaption className="text-sm text-white font-semibold leading-snug">
                          {item.caption || item.alt}
                        </figcaption>
                      </div>
                      <span className="absolute top-3 left-3 inline-flex items-center rounded-full bg-card/90 backdrop-blur px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-foreground/80 border border-border/60">
                        {item.category}
                      </span>
                    </figure>
                  </button>
                </StaggerItem>
              ))}
            </StaggerContainer>
          )}
        </div>
      </section>

      <GalleryLightbox
        items={lightboxItems}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onIndexChange={setLightboxIndex}
      />

      <CTABlock headline="Like the look of the workshop?" subtext="Pop in or book your car in — daily driver, van or something special, we'll take care of it." />
    </>
  );
}

